import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, BadgeCheck, Banknote, Loader2, ShieldCheck } from "lucide-react";

import { createClientRequestId } from "../../utils/clientRequestId";
import { riderApi } from "../services/riderApi";

// WP6 Physical Cash Return, Rider side (target-v2).
//
// The screen has to make one thing unmissable: reporting that the cash went back
// does not settle anything. Until the customer confirms, the money is still the
// Rider's responsibility, and if the customer never confirms it goes to the
// business at remittance. A Rider who believes an assertion clears their count
// will be short at settlement and will not know why.

const formatPhp = (cents) => `₱${(Number(cents || 0) / 100).toLocaleString("en-PH", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

function secondsLeft(expiresAt) {
  if (!expiresAt) return 0;
  return Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000));
}

function Countdown({ expiresAt }) {
  const [remaining, setRemaining] = useState(() => secondsLeft(expiresAt));
  useEffect(() => {
    setRemaining(secondsLeft(expiresAt));
    const timer = setInterval(() => setRemaining(secondsLeft(expiresAt)), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);
  const minutes = Math.floor(remaining / 60);
  const seconds = String(remaining % 60).padStart(2, "0");
  if (remaining <= 0) {
    return <span className="font-semibold text-rose-700 dark:text-rose-300">Window closed</span>;
  }
  return (
    <span className="font-semibold tabular-nums">{minutes}:{seconds} left</span>
  );
}

export default function RiderCashReturnPanel({
  businessId,
  stop,
  cashReturn = null,
  disabled = false,
  onChanged,
}) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [code, setCode] = useState("");

  const cod = stop?.cod || null;
  const collectedCents = Number(cod?.collectedAmountCents || 0);
  const attemptFailed = stop?.attempt?.status === "FAILED";
  const holdsCash = cod?.status === "COLLECTED" && collectedCents > 0;
  const expired = useMemo(
    () => Boolean(cashReturn) && secondsLeft(cashReturn.expiresAt) <= 0,
    [cashReturn],
  );

  if (!holdsCash || !attemptFailed) return null;

  const run = async (label, work) => {
    setBusy(label);
    setError("");
    try {
      const result = await work(createClientRequestId(`cash_return_${label}`));
      onChanged?.(result);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || "Something went wrong.");
    } finally {
      setBusy("");
    }
  };

  const settled = cashReturn && cashReturn.status !== "PENDING";
  const confirmed = cashReturn?.status === "CONFIRMED";

  return (
    <section className="rounded-3xl border border-amber-300 bg-amber-50 p-5 dark:border-amber-500/30 dark:bg-amber-950/20">
      <h3 className="flex items-center gap-2 text-lg font-semibold text-stone-950 dark:text-white">
        <Banknote className="h-5 w-5" aria-hidden="true" />
        Cash you are still holding
      </h3>
      <p className="mt-1 text-sm text-stone-700 dark:text-stone-300">
        You collected {formatPhp(collectedCents)} for this order and the delivery did not
        succeed. That money is still counted against you until it is accounted for.
      </p>

      {!cashReturn && (
        <div className="mt-4">
          <label className="block text-sm font-semibold text-stone-950 dark:text-white">
            Notes for the record (optional)
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={1000}
              placeholder="Handed the cash back at the door"
              className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white"
            />
          </label>
          <p className="mt-3 rounded-2xl bg-white/70 p-3 text-sm text-stone-700 dark:bg-neutral-900/60 dark:text-stone-300">
            Reporting this does not clear the cash from your count. The customer has
            ten minutes to confirm it in their own order. If they do not, you still
            hand the money to the business.
          </p>
          <button
            type="button"
            disabled={disabled || Boolean(busy) || !cod?.version}
            onClick={() => run("assert", (idempotencyKey) => riderApi.assertCashReturn(businessId, stop.attempt.id, {
              expectedCodVersion: cod.version, note, idempotencyKey,
            }))}
            className="mt-4 flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-amber-700 px-5 text-lg font-semibold text-white disabled:opacity-50"
          >
            {busy === "assert" ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Banknote className="h-5 w-5" aria-hidden="true" />}
            I gave the cash back to the customer
          </button>
        </div>
      )}

      {cashReturn && !settled && (
        <div className="mt-4 rounded-2xl border border-amber-300 bg-white p-4 dark:border-amber-500/30 dark:bg-neutral-900">
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold text-stone-950 dark:text-white">
            <span>Waiting for the customer to confirm {formatPhp(cashReturn.returnedAmountCents)}</span>
            <Countdown expiresAt={cashReturn.expiresAt} />
          </p>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
            Ask the customer to open this order and confirm. If they would rather read
            you a code, they can show a six-digit code from the same screen.
          </p>

          {expired ? (
            <p className="mt-4 flex items-start gap-2 rounded-2xl bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              The confirmation window closed. Keep the cash and hand it to the business
              at remittance.
            </p>
          ) : (
            <div className="mt-4">
              <label className="block text-sm font-semibold text-stone-950 dark:text-white">
                Six-digit code from the customer
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 text-2xl tracking-[0.4em] text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white"
                  aria-label="Six-digit code the customer reads to you"
                />
              </label>
              {cashReturn.challengeIssued && cashReturn.challengeAttemptsRemaining <= 2 && (
                <p className="mt-2 text-sm font-semibold text-rose-700 dark:text-rose-300">
                  {cashReturn.challengeAttemptsRemaining === 0
                    ? "No tries left. This cash goes to the business at remittance."
                    : `${cashReturn.challengeAttemptsRemaining} tries left before this is locked.`}
                </p>
              )}
              <button
                type="button"
                disabled={disabled || Boolean(busy) || code.length !== 6 || cashReturn.challengeAttemptsRemaining === 0}
                onClick={() => run("redeem", (idempotencyKey) => riderApi.redeemCashReturn(businessId, cashReturn.cashReturnId, {
                  expectedVersion: cashReturn.version, code, idempotencyKey,
                }))}
                className="mt-4 flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-700 px-5 text-lg font-semibold text-white disabled:opacity-50"
              >
                {busy === "redeem" ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <ShieldCheck className="h-5 w-5" aria-hidden="true" />}
                Enter the customer's code
              </button>
            </div>
          )}
        </div>
      )}

      {confirmed && (
        <p className="mt-4 flex items-start gap-2 rounded-2xl bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
          <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          The customer confirmed {formatPhp(cashReturn.returnedAmountCents)}. This is no
          longer counted against you.
        </p>
      )}

      {settled && !confirmed && (
        <p className="mt-4 flex items-start gap-2 rounded-2xl bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {cashReturn.status === "REFUSED"
            ? `The customer says they did not get the cash back. Keep ${formatPhp(cashReturn.returnedAmountCents)} with your trip cash and hand it to the store. The store refunds the customer.`
            : `The customer did not confirm in time. Keep ${formatPhp(cashReturn.returnedAmountCents)} with your trip cash and hand it to the store.`}
        </p>
      )}

      {error && (
        <p role="alert" className="mt-4 rounded-2xl bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
          {error}
        </p>
      )}
    </section>
  );
}
